import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../layout/DefaultLayout';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import authService from '../services/authService';
import userService from '../services/userService';
import { useGlobalState } from '../state';

import STORAGE_IMG_PLACEHOLDER from '../images/profile.jpg';
import LEY_IMG from'../images/ley.png';
import LOGO_IMG from '../images/logo_nuevo.png';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const Credencial: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(false);
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [titulo, setTitulo] = useState<string | null>(null);
  const [nombreColegio, setNombreColegio] = useState<string | null>(null);
  const [codigoColegio, setCodigoColegio] = useState<string | null>(null);
  const [numColegio, setNumColegio] = useState<string | null>(null);
  const [tomo, setTomo] = useState<string | null>(null);
  const [folio, setFolio] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const isBeneficiario = !!user?.datosUsuario?.beneficiario;

  const getEmissionDateTimeString = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `Documento de emisión digital generado el ${day}/${month}/${year} a las ${hours}:${minutes} hs.`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (user === undefined) return; // auth still checking
        if (!user) {
          navigate('/auth/signin');
          return;
        }

        setLoading(true);
        
        // Try to get profile picture
        try {
          const resp: any = await userService.getProfilePicture(user.datosUsuario.legajoPrevisional);
          // resp may be { data: base64 } or an ArrayBuffer depending on service implementation
          if (!resp) {
            setImageUri(null);
          } else if (typeof resp === 'string') {
            // assume already base64 string
            setImageUri(resp.startsWith('data:') ? resp : `data:image/jpeg;base64,${resp}`);
          } else if (resp.data && typeof resp.data === 'string') {
            setImageUri(resp.data.startsWith('data:') ? resp.data : `data:image/jpeg;base64,${resp.data}`);
          } else if (resp instanceof ArrayBuffer || resp.buffer instanceof ArrayBuffer) {
            const buffer = resp instanceof ArrayBuffer ? resp : resp.buffer;
            const base64 = arrayBufferToBase64(buffer);
            setImageUri(`data:image/jpeg;base64,${base64}`);
          } else {
            // fallback
            setImageUri(null);
          }
        } catch (imgErr) {
          // ignore image errors, show placeholder
          setImageUri(null);
        }

        // Fill textual fields from user or fetch details if missing
        if (isBeneficiario) {
          // No dynamic data needed for beneficiario
        } else if (user && (!user.datosUsuario.titulo || !user.datosUsuario.nombreColegio)) {
          try {
            const res: any = await userService.getDatosDinamicos(false);
            if (res?.success) {
              const afiliado = res.data?.afiliado ?? res.data;
              if (afiliado) {
                setTitulo(afiliado.titulo ?? afiliado?.Titulo ?? null);
                setTomo(afiliado.tomo ?? afiliado?.Tomo ?? null);
                setFolio(afiliado.folio ?? afiliado?.Folio ?? null);
                setCodigoColegio(afiliado.codigoColegioActual ?? afiliado?.CodigoColegioActual ?? null);
                setNumColegio(afiliado.numCol ?? afiliado?.NumCol ?? null);
                setNombreColegio(afiliado.nombreColegio ?? afiliado?.NombreColegio ?? null);
              }
            } else {
              // don't fail the page; just show what's available
            }
          } catch (e) {
            // ignore
          }
        } else if (user) {
          setTitulo(user.datosUsuario.titulo ?? null);
          setTomo(user.datosUsuario.tomo ?? null);
          setFolio(user.datosUsuario.folio ?? null);
          setCodigoColegio(user.datosUsuario.codigoColegioActual ?? null);
          setNumColegio(user.datosUsuario.numCol ?? null);
          setNombreColegio(user.datosUsuario.nombreColegio ?? null);
        }
      } catch (ex: any) {
        setError('No se pudo cargar la credencial. Intenta nuevamente.');
        console.error(ex);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
      <DefaultLayout>
          <div className="print:hidden">
              <Breadcrumb pageName="Credencial Digital" />
          </div>

          {error && <div className="mt-4 print:hidden"><Alert type="failure" message={error} /></div>}
          
          <div id="printable-card" className="max-w-6xl md:mx-auto bg-white md:p-6 justify-center ">    
              {isBeneficiario && (
                  <div className="hidden print:block text-center mb-6">
                      <h1 className="text-2xl font-bold text-gray-800">Carnet de Beneficiario</h1>
                  </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                  <div className="flex items-start w-full md:w-[430px] mx-auto">
                      <div className="flex items-start border rounded-lg border">
                          <div className="w-1/2">
                              <div className="rounded-tl-lg rounded-bl-lg overflow-hidden bg-transparent">
                                  {loading ? (
                                      <div className="flex items-center justify-center h-48 bg-gray-100">
                                          <Loading statusText="Cargando" />
                                      </div>
                                  ) : (
                                      <img
                                          src={imageUri ?? STORAGE_IMG_PLACEHOLDER}
                                          alt="Foto perfil"
                                          className="w-full h-48 object-cover"
                                      />
                                  )}
                              </div>
                          </div>

                          <div className="bg-primario-azul-400 flex items-center justify-center">
                              <img src={LEY_IMG} alt="Ley" style={{ width: 61 }} />
                          </div>

                          <div className="w-5/12 bg-gray-50 rounded-tr-lg rounded-br-lg flex items-center justify-center p-4">
                              <img src={LOGO_IMG} alt="Logo" className="w-36 h-auto object-contain" />
                          </div>
                      </div>

                  </div>

                  <div className="bg-gray-100 rounded-lg p-4 mt-6">
                      {!isBeneficiario && (
                          <>
                              <div className="flex items-center justify-between mb-4">
                                  <div className="text-lg font-semibold">{nombreColegio ?? 'No Disponible'}</div>
                                  {codigoColegio && (
                                      <img
                                          src={`${process.env.REACT_APP_API_URL ?? ''}/assets/logos/${codigoColegio}.jpg`}
                                          alt="Logo colegio"
                                          className="h-24 w-24 object-contain rounded"
                                      />
                                  )}
                              </div>

                              <div className="border-t border-gray-200 my-3" />
                          </>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                          {/* Bloque Invariante (Superior) */}
                          <div className="text-sm text-gray-600">Nombre</div>
                          <div className="text-sm font-semibold">{`${user?.datosUsuario.apellido ?? ''} ${user?.datosUsuario.nombre ?? ''}`.trim()}</div>

                          <div className="text-sm text-gray-600">Documento (DNI)</div>
                          <div className="text-sm font-semibold">{user?.datosUsuario.dni ?? '—'}</div>

                          <div className="text-sm text-gray-600">Estado</div>
                          <div className="text-sm font-semibold">Activo</div>

                          {/* Línea divisoria entre bloques */}
                          <div className="col-span-2 border-t border-gray-200 my-2" />

                          {/* Bloque Variante (Inferior) */}
                          {!isBeneficiario ? (
                              <>
                                  <div className="text-sm text-gray-600">Título</div>
                                  <div className="text-sm font-semibold">{titulo ?? 'No Disponible'}</div>

                                  <div className="text-sm text-gray-600">Tomo</div>
                                  <div className="text-sm font-semibold">{tomo ?? '—'}</div>

                                  <div className="text-sm text-gray-600">Folio</div>
                                  <div className="text-sm font-semibold">{folio ?? '—'}</div>

                                  <div className="text-sm text-gray-600">Leg. Col.</div>
                                  <div className="text-sm font-semibold">{numColegio ?? '—'}</div>

                                  <div className="text-sm text-gray-600">Leg. Prev.</div>
                                  <div className="text-sm font-semibold">{user?.datosUsuario.legajoPrevisional ?? '—'}</div>
                              </>
                          ) : (
                              <>
                                  <div className="text-sm text-gray-600">Tipo de Beneficio</div>
                                  <div className="text-sm font-semibold">{user?.datosUsuario.beneficiario?.tipoBeneficiario ?? '—'}</div>

                                  <div className="text-sm text-gray-600">Número de Beneficiario</div>
                                  <div className="text-sm font-semibold">{user?.datosUsuario.legajoPrevisional ?? '—'}</div>
                              </>
                          )}
                      </div>
                  </div>
              </div>                             

              {isBeneficiario && (
                  <div className="hidden print:block text-center mt-6 text-[11px] text-gray-500 font-normal border-t border-gray-300 pt-4 leading-relaxed">
                      <p className="font-medium text-gray-600">{getEmissionDateTimeString()}</p>
                      <p className="mt-2 text-gray-500 max-w-[380px] mx-auto">La presente credencial digital posee validez temporal por el término de seis (6) meses contados a partir de su fecha de emisión.</p>
                  </div>
              )}
          </div>

          <div className="mt-6 flex justify-center gap-3 print:hidden">
              <button
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 rounded border border-gray-300 bg-white"
              >
                  Volver
              </button>
              {isBeneficiario && (
                  <button
                      onClick={() => window.print()}
                      className="px-4 py-2 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  >
                      Compartir Credencial
                  </button>
              )}
          </div>

          {isBeneficiario && (
              <style>{`
                  @media print {
                      body * {
                          visibility: hidden;
                      }
                      #printable-card, #printable-card * {
                          visibility: visible;
                      }
                      aside, header, button, .print\\:hidden, .print-hidden {
                          display: none !important;
                      }
                      html, body, #root, .flex, .relative, main, main > div {
                          height: auto !important;
                          min-height: 0 !important;
                          overflow: visible !important;
                          position: static !important;
                          margin: 0 !important;
                          padding: 0 !important;
                          background: transparent !important;
                          box-shadow: none !important;
                      }
                      #printable-card {
                          position: relative !important;
                          margin: 40px auto !important;
                          padding: 24px !important;
                          width: 450px !important;
                          max-width: 450px !important;
                          box-shadow: none !important;
                          border: 1px solid #cbd5e1 !important;
                          border-radius: 12px !important;
                          background-color: #ffffff !important;
                          page-break-inside: avoid !important;
                          break-inside: avoid !important;
                      }
                      * {
                          -webkit-print-color-adjust: exact !important;
                          print-color-adjust: exact !important;
                      }
                  }
              `}</style>
          )}
      </DefaultLayout>
  );
};

export default Credencial;